




















































import { computed, defineComponent, ref, set, watch } from "@vue/composition-api";
import useAuthUser from "@/hooks/useAuthUser";
import { useBecomeCreatorState } from "@/hooks/useBecomeCreatorState";
import useFiles from "@/hooks/useFiles";
import useFullCreatorApplicationUpload, {
  FullCreatorApplicationUploadStatus,
} from "@/hooks/useFullCreatorApplicationUpload";
import { $t } from "@/lib/i18nHelpers";
import { formatDateOnly } from "@/lib/time";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseButton from "@/components/BaseButton";
import BaseStepper from "@/components/BaseStepper";
import PageTitle from "@/components/PageTitle";
import PayoutForm from "@/components/PayoutForm";
import CertificationForm from "./components/CertificationForm.vue";
import ResidenceForm from "./components/ResidenceForm.vue";
import TaxpayerInfoForm from "./components/TaxpayerInfoForm.vue";
import TinForm from "./components/TinForm.vue";
import VideosForm from "./components/VideosForm.vue";
import { generateTaxFormCsv } from "./generateTaxFormCsv";
import { StepData, TaxFormDraft } from "../../types";
import PayeeInfoButton from "../PayeeInfoButton.vue";

export default defineComponent({
  name: "FullCreatorForm",
  components: { PageTitle, PayeeInfoButton, BaseButton, BaseStepper, BaseIcon },
  setup(_, { emit, root }) {
    const { authUserStrict } = useAuthUser();
    const {
      lastStep,
      taxFormDraft: persistedTaxFormDraft,
      update: updateBecomeCreatorState,
      getData: getBecomeCreatorState,
    } = useBecomeCreatorState();
    const { status, startRetryEditing } = useFullCreatorApplicationUpload();
    const { uploadThenUpdate } = useFiles();
    const submits = ref<Record<string, () => Promise<any>>>({});
    const valid = ref(false);
    const loading = ref(false);
    const sendingData = ref(false);
    const initialized = ref(false);
    const applicationUploadFailed = computed(() => status.value === FullCreatorApplicationUploadStatus.ERROR);
    const taxFormDraft = ref<TaxFormDraft | null>(null);

    const steps: StepData[] = [
      {
        name: "country",
        step: 1,
        component: ResidenceForm,
        required: true,
      },
      {
        name: "taxpayerInfo",
        step: 2,
        component: TaxpayerInfoForm,
        required: true,
      },
      {
        name: "tin",
        step: 3,
        component: TinForm,
        required: true,
      },
      {
        name: "certification",
        step: 4,
        component: CertificationForm,
        required: true,
        buttonLabel: $t("Sign and continue"),
      },
      {
        name: "videos",
        step: 5,
        component: VideosForm,
        required: true,
      },
      {
        name: "payoutInfo",
        step: 6,
        component: PayoutForm,
        required: true,
      },
    ];
    const videosStepIndex = steps.findIndex(step => step.name === "videos");

    const currentStep = ref<StepData>(steps[0]);
    const getStepTemplateName = (step: number) => "step-" + step;

    const consumeRetryRequest = (includeUploadError = false) => {
      const retryFromRoute = root.$route?.query?.retryFullCreatorUpload === "1";
      const retryStarted = status.value === FullCreatorApplicationUploadStatus.RETRYING;
      const failedSessionOnMount = includeUploadError && status.value === FullCreatorApplicationUploadStatus.ERROR;
      if (!retryFromRoute && !retryStarted && !failedSessionOnMount) return false;

      if (status.value === FullCreatorApplicationUploadStatus.ERROR) {
        startRetryEditing();
      }
      currentStep.value = steps[videosStepIndex];
      emit("showPromote", false);

      if (retryFromRoute) {
        const query = { ...root.$route.query };
        delete query.retryFullCreatorUpload;
        void root.$router.replace({ query }).catch(() => undefined);
      }
      return true;
    };

    const handleTitleClick = async () => {
      if (currentStep.value.step === 1) {
        emit("showPromote", true);
      } else {
        emit("showPromote", false);
        currentStep.value = steps[currentStep.value.step - 2];
      }
    };

    const handleButtonClick = async (hasNext: boolean, step: number, skip?: boolean) => {
      const stepInProcess = steps[step - 1];
      if (!stepInProcess) return;
      if (!hasNext && applicationUploadFailed.value) return;
      const saveProgressBeforeSubmit =
        hasNext && stepInProcess.name === "videos" && !stepInProcess.doNotSaveStepProgress;
      let progressSavedBeforeSubmit = false;

      if (!skip) {
        try {
          sendingData.value = true;
          if (saveProgressBeforeSubmit) {
            await updateBecomeCreatorState(stepInProcess.step + 1, taxFormDraft.value);
            progressSavedBeforeSubmit = true;
          }
          await submits.value[stepInProcess.name]?.();
        } catch (e) {
          if (progressSavedBeforeSubmit) {
            await updateBecomeCreatorState(stepInProcess.step, taxFormDraft.value);
          }
          /* eslint-disable no-console */
          console.error(e);
          return;
        } finally {
          sendingData.value = false;
        }
      }

      if (hasNext) {
        loading.value = false;
        currentStep.value = steps[currentStep.value.step];
      } else {
        emit("startVerification");
      }
      if (hasNext && !currentStep.value.doNotSaveStepProgress && !progressSavedBeforeSubmit) {
        updateBecomeCreatorState(currentStep.value.step, taxFormDraft.value);
      }
    };

    const setCb = (stepName: string, cb: () => Promise<any>) => {
      set(submits.value, stepName, cb);
    };

    const updateDraft = (partial: Partial<TaxFormDraft>) => {
      taxFormDraft.value = { ...(taxFormDraft.value ?? {}), ...partial };
    };

    const submitTaxForm = async () => {
      if (!taxFormDraft.value) return;
      const file = generateTaxFormCsv(taxFormDraft.value, formatDateOnly(new Date(), "MM/dd/yyyy"));
      await uploadThenUpdate({
        fileId: { subjectId: authUserStrict.value.id, type: "tax_form", fileNumber: 0 },
        file,
      });
    };
    set(submits.value, "certification", submitTaxForm);

    const getData = async () => {
      try {
        loading.value = true;
        await getBecomeCreatorState();
        taxFormDraft.value = persistedTaxFormDraft.value;
        initialized.value = true;
        if (consumeRetryRequest(true)) return;
        if (lastStep.value > 1) {
          // No draft means progress saved before the in-built tax form existed —
          // restart those users from the first step so they fill the form.
          currentStep.value = taxFormDraft.value ? steps[lastStep.value - 1] : steps[0];
          emit("showPromote", false);
        }
      } finally {
        loading.value = false;
      }
    };

    watch([() => root.$route?.query?.retryFullCreatorUpload, status], () => {
      if (initialized.value) consumeRetryRequest();
    });

    getData();

    return {
      steps,
      currentStep,
      getStepTemplateName,
      handleTitleClick,
      handleButtonClick,
      setCb,
      updateDraft,
      taxFormDraft,
      loading,
      valid,
      sendingData,
      applicationUploadFailed,
    };
  },
});
